import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { uploadAsset } from '../../lib/assetsApi';

const { FiUpload, FiX, FiCheck, FiImage } = FiIcons;

const QuickUploader = () => {
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const addUpload = (files) => {
    const newUploads = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'pending',
      result: null,
      error: null
    }));
    setUploads(prev => [...prev, ...newUploads]);
  };

  const handleFileSelect = (event) => {
    addUpload(event.target.files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    addUpload(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const uploadFile = async (uploadId) => {
    const upload = uploads.find(u => u.id === uploadId);
    if (!upload) return;

    setUploads(prev => prev.map(u => 
      u.id === uploadId ? { ...u, status: 'uploading' } : u
    ));

    try {
      const result = await uploadAsset(upload.file, 'quick-upload');
      setUploads(prev => prev.map(u => 
        u.id === uploadId ? { ...u, status: 'completed', result } : u
      ));
    } catch (error) {
      setUploads(prev => prev.map(u => 
        u.id === uploadId ? { ...u, status: 'error', error: error.message } : u
      ));
    }
  };

  const removeUpload = (uploadId) => {
    setUploads(prev => prev.filter(u => u.id !== uploadId));
  };

  const uploadAll = () => {
    uploads
      .filter(u => u.status === 'pending')
      .forEach(u => uploadFile(u.id));
  };

  const clearAll = () => {
    setUploads([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'completed':
        return <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />;
      case 'error':
        return <SafeIcon icon={FiX} className="w-4 h-4 text-red-600" />;
      default:
        return <SafeIcon icon={FiImage} className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Quick Uploader</h1>
          <p className="text-gray-600">Quickly upload multiple files to test storage</p>
        </div>

        {/* Upload Area */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            Drop files here or click to browse
          </p>
          <p className="text-gray-600 mb-4">Upload multiple files at once</p>
          
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="quick-upload"
          />
          <label
            htmlFor="quick-upload"
            className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            <SafeIcon icon={FiUpload} className="w-4 h-4" />
            <span>Select Files</span>
          </label>
        </div>

        {/* Controls */}
        {uploads.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-gray-600">
              {uploads.length} file{uploads.length !== 1 ? 's' : ''} queued
            </div>
            <div className="flex space-x-3">
              <button
                onClick={uploadAll}
                disabled={uploads.every(u => u.status !== 'pending')}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon icon={FiUpload} className="w-4 h-4" />
                <span>Upload All</span>
              </button>
              <button
                onClick={clearAll}
                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
                <span>Clear All</span>
              </button>
            </div>
          </div>
        )}

        {/* Upload List */}
        {uploads.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Upload Queue</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {uploads.map((upload) => (
                <div key={upload.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {getStatusIcon(upload.status)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{upload.name}</p>
                        <p className="text-sm text-gray-600">{formatFileSize(upload.size)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {upload.status === 'pending' && (
                        <button
                          onClick={() => uploadFile(upload.id)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Upload
                        </button>
                      )}
                      {upload.status === 'completed' && upload.result?.url && (
                        <a
                          href={upload.result.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                          View
                        </a>
                      )}
                      <button
                        onClick={() => removeUpload(upload.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {upload.error && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                      {upload.error}
                    </div>
                  )}
                  
                  {upload.result && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                      <p className="text-green-700 font-mono text-xs break-all">
                        {upload.result.path}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default QuickUploader;